import { Form, Formik } from "formik";
import { useAppDispatch } from "../../Store/hooks";
import { showLoader } from "../../Store/loaderSlice";
import {
  Box,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Avatar,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  PositionDto,
  RoleDto,
  SportDto,
  UserFormData,
} from "../../Dto/Generic";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
} from "../../Utils/ApiService";
import { showToaster } from "../../Store/toastSlice";
import { DisplayErrorMessages } from "../../Utils/HelperService";
import { userFormValidationByRole } from "./userFormValidation";

interface UserFormModalProps {
  userData: UserFormData;
  resetForm: () => void;
  getUsers: () => void;
}

const getRoleSpecificFields = (roleName: string) => {
  const normalized = (roleName || "").toLowerCase();
  switch (normalized) {
    case "player":
      return [
        "height",
        "weight",
        "dateOfBirth",
        "position",
        "preferredPosition",
        "dominantFoot",
        "skills",
        "city",
        "state",
        "country",
        "nationality",
        "description",
        "sports",
      ];
    case "scout":
    case "coach":
      return [
        "dateOfBirth",
        "club",
        "city",
        "state",
        "country",
        "nationality",
        "height",
        "weight",
        "position",
        "preferredPosition",
        "skills",
        "dominantFoot",
        "sports",
      ];
    default:
      return [];
  }
};

const UserFormModal = ({
  userData,
  resetForm,
  getUsers,
}: UserFormModalProps) => {
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [sports, setSports] = useState<SportDto[]>([]);
  const [positions, setPositions] = useState<PositionDto[]>([]);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>("");

  const getRoles = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(
        "roles?ismobile=false&page=1&limit=100&sort=displayName&sortDirection=ASC"
      );
      setRoles(res.data.data.records);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setRoles([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch]);

  const getSports = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(
        "sports?page=1&limit=100&sort=createdAt&sortDirection=ASC"
      );
      setSports(res.data.data.rows);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setSports([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch]);

  const fetchPositionBasedOnSport = useCallback(
    async (sportId: string) => {
      try {
        dispatch(showLoader({ showLoader: true }));
        if (sportId) {
          const res = await GetApiMethod(`sports/all/${sportId}`);
          setPositions(res.data.data.others);
        }
        dispatch(showLoader({ showLoader: false }));
      } catch (error) {
        setPositions([]);
        dispatch(showLoader({ showLoader: false }));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    getRoles();
    getSports();
  }, [getRoles]);

  useEffect(() => {
    if (userData.id) {
      fetchPositionBasedOnSport(userData.sports || "");
    }
  }, [userData.id]);

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = useCallback(
    async (values: UserFormData) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        // For creating new users, use FormData to handle file uploads
        if (!values.id) {
          const formData = new FormData();
          formData.append("email", values.email);
          formData.append("firstName", values.firstName);
          formData.append("lastName", values.lastName);
          formData.append("phoneNumber", values.phone);
          formData.append("roles[]", values.roleId);

          // Add optional fields if provided
          if (values.height) formData.append("height", values.height);
          if (values.weight) formData.append("weight", values.weight);
          if (values.dateOfBirth)
            formData.append("dateOfBirth", values.dateOfBirth);
          if (values.position) formData.append("position", values.position);
          if (values.sports) formData.append("sports", values.sports);
          if (values.preferredPosition)
            formData.append("preferredPosition", values.preferredPosition);
          if (values.dominantFoot)
            formData.append("dominantFoot", values.dominantFoot);
          if (values.skills) formData.append("skills", values.skills);
          if (values.club) formData.append("club", values.club);
          if (values.city) formData.append("city", values.city);
          if (values.state) formData.append("state", values.state);
          if (values.country) formData.append("country", values.country);
          if (values.nationality)
            formData.append("nationality", values.nationality);
          if (values.description)
            formData.append("description", values.description);

          // Add profile image if selected
          if (profileImageFile) {
            formData.append("files", profileImageFile);
          }

          await PostApiMethod("user", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          // For editing existing users, use regular JSON
          const finalValues: any = {
            ...values,
            phoneNumber: values.phone,
            roles: values.roles.map((r) => r.id),
          };

          await PutApiMethod("user/" + finalValues.id, finalValues);
        }

        resetForm();
        dispatch(showLoader({ showLoader: false }));

        // Reset profile image state
        setProfileImageFile(null);
        setProfileImagePreview("");

        getUsers();

        dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: !values.id
              ? "User created successfully"
              : "User updated successfully",
            showDialog: true,
            timer: "3000",
          })
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, profileImageFile]
  );

  return (
    <Formik
      initialValues={userData}
      validationSchema={userFormValidationByRole}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
    >
      {({
        handleSubmit,
        handleChange,
        values,
        errors,
        touched,
        setFieldValue,
      }) => {
        const fields = getRoleSpecificFields(
          roles.find((role) => role.id === values.roleId)?.roleName || ""
        );

        let existingPosition = "";
        const handleSportsChange = (e: any) => {
          setFieldValue("sports", e.target.value);

          if (e.target.value !== existingPosition) {
            setFieldValue("position", "");
            fetchPositionBasedOnSport(e.target.value);
          }
          existingPosition = e.target.value;
        };

        const handleRoleChange = (e: any) => {
          setFieldValue("roleId", e.target.value);
          const selectedRole = roles.find((role) => role.id === e.target.value);
          setFieldValue("roleName", selectedRole?.roleName || "");

          // Clear role-specific fields when role changes
          setFieldValue("height", "");
          setFieldValue("weight", "");
          setFieldValue("dateOfBirth", "");
          setFieldValue("position", "");
          setFieldValue("preferredPosition", "");
          setFieldValue("dominantFoot", "");
          setFieldValue("skills", "");
          setFieldValue("club", "");
          setFieldValue("city", "");
          setFieldValue("state", "");
          setFieldValue("nationality", "Australian");
          setFieldValue("description", "");
          setFieldValue("sports", "");
        };

        return (
          <>
            <Box className="modal-body">
              <Form onSubmit={handleSubmit}>
                <Grid item md={12} m={2}>
                  {/* Static Field Start */}
                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold" required>
                      First Name:
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Type here..."
                      variant="standard"
                      value={values.firstName}
                      name="firstName"
                      onChange={handleChange}
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="text-danger">{`${`${errors.firstName}`}`}</div>
                    )}
                  </Grid>

                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold" required>
                      Last Name:
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Type here..."
                      variant="standard"
                      value={values.lastName}
                      name="lastName"
                      onChange={handleChange}
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="text-danger">{`${errors.lastName}`}</div>
                    )}
                  </Grid>

                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold" required>
                      Email:
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Type here..."
                      variant="standard"
                      value={values.email}
                      name="email"
                      onChange={handleChange}
                    />
                    {touched.email && errors.email && (
                      <div className="text-danger">{`${errors.email}`}</div>
                    )}
                  </Grid>

                  <Grid container spacing={1} className="p-3">
                    <FormLabel className="fw-bold" required>
                      Phone:
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Type here..."
                      variant="standard"
                      value={values.phone}
                      name="phone"
                      onChange={handleChange}
                    />
                    {touched.phone && errors.phone && (
                      <div className="text-danger">{`${errors.phone}`}</div>
                    )}
                  </Grid>
                  {/* Static Field End */}

                  {/* Status Field */}
                  {userData?.id && (
                    <Grid
                      container
                      spacing={1}
                      alignItems={"center"}
                      className="p-3"
                    >
                      <FormLabel className="fw-bold" style={{ marginRight: 8 }}>
                        Status:
                      </FormLabel>
                      <Switch
                        name="status"
                        checked={values.status} // true = Active, false = InActive
                        onChange={handleChange}
                        color="primary"
                        disabled={values.emailVerified === false}
                      />
                    </Grid>
                  )}

                  {!(userData.roles[0]?.roleName === "super_admin") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Role:
                      </FormLabel>
                      <Select
                        fullWidth
                        variant="standard"
                        value={values.roleId || ""}
                        onChange={handleRoleChange}
                        placeholder="Select Role"
                      >
                        {roles.map((role: RoleDto) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.roleId && errors.roleId && (
                        <div className="text-danger">{`${errors.roleId}`}</div>
                      )}
                    </Grid>
                  )}

                  {/* Role-specific fields */}
                  {fields.includes("height") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Height (cm):
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="e.g. 180"
                        variant="standard"
                        value={values.height || ""}
                        name="height"
                        onChange={handleChange}
                      />
                      {touched.height && errors.height && (
                        <div className="text-danger">{`${errors.height}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("weight") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Weight (kg):
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="e.g. 75"
                        variant="standard"
                        value={values.weight || ""}
                        name="weight"
                        onChange={handleChange}
                      />
                      {touched.weight && errors.weight && (
                        <div className="text-danger">{`${errors.weight}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("dateOfBirth") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Date of Birth:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        type="date"
                        variant="standard"
                        value={(values.dateOfBirth || "")
                          .toString()
                          .slice(0, 10)}
                        name="dateOfBirth"
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                      {touched.dateOfBirth && errors.dateOfBirth && (
                        <div className="text-danger">{`${errors.dateOfBirth}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("sports") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Sports:
                      </FormLabel>
                      <Select
                        fullWidth
                        variant="standard"
                        value={values.sports || ""}
                        onChange={handleSportsChange}
                        placeholder="Select Sports"
                      >
                        {sports.map((sport: SportDto) => (
                          <MenuItem key={sport.id} value={sport.id}>
                            {sport.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.sports && errors.sports && (
                        <div className="text-danger">{`${errors.sports}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("position") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Position:
                      </FormLabel>
                      <Select
                        fullWidth
                        variant="standard"
                        value={values.position || ""}
                        onChange={(e) =>
                          setFieldValue("position", e.target.value)
                        }
                        placeholder="Select Position"
                        disabled={positions.length === 0}
                      >
                        {positions.map((position: PositionDto) => (
                          <MenuItem key={position.id} value={position.id}>
                            {position.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {values.sports && positions.length === 0 && (
                        <div className="text-danger">
                          No positions available for the selected sport.
                        </div>
                      )}
                      {values.sports &&
                        positions.length !== 0 &&
                        touched.position &&
                        errors.position && (
                          <div className="text-danger">{`${errors.position}`}</div>
                        )}
                    </Grid>
                  )}

                  {fields.includes("preferredPosition") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Preferred Position:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="e.g. Forward"
                        variant="standard"
                        value={values.preferredPosition || ""}
                        name="preferredPosition"
                        onChange={handleChange}
                      />
                      {touched.preferredPosition &&
                        errors.preferredPosition && (
                          <div className="text-danger">
                            {`${errors.preferredPosition}`}
                          </div>
                        )}
                    </Grid>
                  )}

                  {fields.includes("dominantFoot") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Dominant Foot:
                      </FormLabel>
                      <TextField
                        select
                        className="w-100"
                        variant="standard"
                        value={values.dominantFoot || ""}
                        name="dominantFoot"
                        onChange={handleChange}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </TextField>
                      {touched.dominantFoot && errors.dominantFoot && (
                        <div className="text-danger">
                          {`${errors.dominantFoot}`}
                        </div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("skills") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Skills:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="e.g. Passing, Shooting, Defense"
                        variant="standard"
                        multiline
                        rows={2}
                        value={values.skills || ""}
                        name="skills"
                        onChange={handleChange}
                      />
                      {touched.skills && errors.skills && (
                        <div className="text-danger">{`${errors.skills}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("club") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Club:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.club || ""}
                        name="club"
                        onChange={handleChange}
                      />
                      {touched.club && errors.club && (
                        <div className="text-danger">{`${`${errors.club}`}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("city") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        City:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.city || ""}
                        name="city"
                        onChange={handleChange}
                      />
                      {touched.city && errors.city && (
                        <div className="text-danger">{`${errors.city}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("state") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        State:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.state || ""}
                        name="state"
                        onChange={handleChange}
                      />
                      {touched.state && errors.state && (
                        <div className="text-danger">{`${errors.state}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("country") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">Country:</FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Australia"
                        variant="standard"
                        value={values.country}
                        name="country"
                        onChange={handleChange}
                        disabled
                      />
                    </Grid>
                  )}

                  {fields.includes("nationality") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Nationality:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.nationality || ""}
                        name="nationality"
                        onChange={handleChange}
                      />
                      {touched.nationality && errors.nationality && (
                        <div className="text-danger">{`${errors.nationality}`}</div>
                      )}
                    </Grid>
                  )}

                  {fields.includes("description") && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold" required>
                        Description:
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        multiline
                        rows={3}
                        value={values.description || ""}
                        name="description"
                        onChange={handleChange}
                      />
                      {touched.description && errors.description && (
                        <div className="text-danger">{`${errors.description}`}</div>
                      )}
                    </Grid>
                  )}

                  {/* Profile Image Field */}
                  {!userData.id && values.roleId && (
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">Profile Image:</FormLabel>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="profile-image-input"
                            type="file"
                            onChange={handleProfileImageChange}
                          />
                          <label htmlFor="profile-image-input">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                const input = document.getElementById(
                                  "profile-image-input"
                                ) as HTMLInputElement;
                                input?.click();
                              }}
                            >
                              Choose Image
                            </button>
                          </label>
                        </Grid>
                        {profileImagePreview && (
                          <Grid>
                            <Avatar
                              src={profileImagePreview}
                              sx={{ width: 80, height: 80 }}
                              alt="Profile Preview"
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                <Box className="d-flex justify-content-end mt-3 p-3">
                  <button className="btn btn-danger" type="submit">
                    Submit
                  </button>
                </Box>
              </Form>
            </Box>
          </>
        );
      }}
    </Formik>
  );
};

export default UserFormModal;
